<script lang="ts">// Importing icons used in the dropdown
import { ArrowIcon, LocalIcon, RemoteIcon } from "@infinitro/icons";
import Minus from "./icons/minus.svelte";
import Plus from "./icons/plus.svelte";
import { stateUi } from "@infinitro/config";
import { onMount, tick } from "svelte";
export let variant = "default";
export let icon = null;
export let title = "";
export let text = "";
export let additionalContent = "";
export let open = false;
export let dropdownOpen = open;
export let isHtml = false;
export let liquidGlassStyle = false;
export let onToggle = undefined;
export let rightTitle = "";
export let panelPadding = "1.25rem 0.5rem 0";
export let expandedBackground = false;
export let links = [];
let lastOpen = open;
let dropdownMenu;
let dropdownMenuContent;
let panelMaxHeight = "0px";
const updatePanelMaxHeight = async (isOpen = dropdownOpen) => {
	await tick();
	if (isOpen !== dropdownOpen) return;
	panelMaxHeight = isOpen && dropdownMenu ? `${dropdownMenu.scrollHeight}px` : "0px";
};
const toggleDropdown = () => {
	if (onToggle) {
		onToggle(!dropdownOpen);
		return;
	}
	if (dropdownOpen) {
		dropdownOpen = false;
		open = false;
		return;
	}
	dropdownOpen = !dropdownOpen;
	open = dropdownOpen;
};
$: useRemoteIcons = $stateUi.accordion?.useRemoteIcons;
$: useAlternative = $stateUi.accordion?.accordionStyle === "alternative";
$: useAlternative4 = $stateUi.accordion?.accordionStyle === "alternative4";
$: useMomentum = $stateUi.accordion?.accordionStyle === "momentum";
$: if (open !== lastOpen) {
	dropdownOpen = open;
	lastOpen = open;
}
$: void updatePanelMaxHeight(dropdownOpen);
onMount(() => {
	if (typeof ResizeObserver === "undefined" || !dropdownMenuContent) return;
	const observer = new ResizeObserver(() => {
		if (dropdownOpen) void updatePanelMaxHeight();
	});
	observer.observe(dropdownMenuContent);
	return () => observer.disconnect();
});
</script>

<div
	class:custom-dropdown-alternative={useAlternative}
	class:custom-dropdown-alternative-4={(dropdownOpen && useAlternative4) || useMomentum}
	class:custom-dropdown-momentum={useMomentum}
	class:custom-dropdown-momentum-open={dropdownOpen && useMomentum}
	class:custom-dropdown-open-alternative={dropdownOpen && (useAlternative || useAlternative4)}
	class:expanded-bg={expandedBackground}
	class:custom-dropdown={!liquidGlassStyle}
	class:custom-dropdown-glass={liquidGlassStyle}
	class={dropdownOpen
		? liquidGlassStyle
			? 'custom-dropdown-open-glass'
			: 'custom-dropdown-open'
		: ''}
>
	<!-- svelte-ignore a11y-click-events-have-key-events -->
	<!-- svelte-ignore a11y-no-static-element-interactions -->
	<div class="dropdown-toggle" on:click={toggleDropdown}>
		<div class="d-flex">
			{#if variant === 'alternative'}
				<svelte:component this={dropdownOpen ? Minus : Plus} />
			{:else}
				<svelte:component this={icon} />
			{/if}
			{#if useMomentum}
				<p class="uppercase">{title}</p>
			{:else}
				<p>{title}</p>
			{/if}
		</div>
		<div class="right-title-arrow-container">
			{#if rightTitle}
				{@html rightTitle}
			{/if}
			<div
				class:rotated={dropdownOpen && variant !== 'alternative'}
				class:arrow-alternative-4={useAlternative4}
			>
				{#if useRemoteIcons}
					<RemoteIcon
						icon="ArrowIcon"
						color={useMomentum ? 'var(--color-accent-500)' : ''}
						size={20}
					/>
				{:else if variant === 'alternative'}
					<svelte:component this={icon} />
				{:else if variant === 'default' && useAlternative4}
					<LocalIcon icon="ArrowIcon" />
				{:else}
					<ArrowIcon />
				{/if}
			</div>
		</div>
	</div>
	<div
		bind:this={dropdownMenu}
		class:expanded={dropdownOpen}
		class:expanded-alternative-4={dropdownOpen && useAlternative4}
		class:expanded-momentum={dropdownOpen && useMomentum}
		class="dropdown-menu"
		class:dropdown-menu-momentum={useMomentum}
		class:dropdown-menu-alternative-4={useAlternative4}
		style={`max-height: ${panelMaxHeight};`}
	>
		<div
			bind:this={dropdownMenuContent}
			class:content-visible={dropdownOpen}
			class="dropdown-menu-content"
			style={useMomentum ? `padding: 0.5rem;` : `padding: ${panelPadding};`}
		>
			{#if isHtml && text}
				<p class="text">{@html text}</p>
			{:else if text}
				<p class="text">{text}</p>
			{/if}
			{#if additionalContent}
				<div class="additional-content">{@html additionalContent}</div>
			{:else if links.length > 0}
				<div class="additional-content" class:additional-content-alternative-4={useAlternative4}>
					{#each links as item, i}
						{#if item.onClick}
							<button
								type="button"
								class="link link-button"
								class:link-alternative-4={useAlternative4}
								on:click|preventDefault={item.onClick}>{item.label}</button
							>
						{:else if item.external}
							<a
								href={item.href}
								target="_blank"
								rel="noopener noreferrer"
								class="link"
								class:link-alternative-4={useAlternative4}>{item.label}</a
							>
						{:else}
							<a
								href={item.href}
								data-sveltekit-reload={item.reload}
								class="link"
								class:link-alternative-4={useAlternative4}>{item.label}</a
							>
						{/if}
					{/each}
				</div>
			{/if}
			<slot name="content" />
		</div>
	</div>
</div>

<style>
	.arrow-alternative-4 {
		color: var(--color-text-700);
}
	.custom-dropdown {
		position: relative;
		height: -moz-fit-content;
		height: fit-content;
		min-height: 54px;
		width: 100%;
		border-radius: 0.75rem;
		border-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-100);
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
}

	.custom-dropdown-glass {
		position: relative;
		isolation: isolate;
		height: -moz-fit-content;
		height: fit-content;
		min-height: 54px;
		width: 100%;
		border-radius: 0.75rem;
		border-width: 1px;
		border-color: double;
		--tw-border-opacity: 1;
		border-color: rgb(169 169 169 / var(--tw-border-opacity, 1));
		font-size: 0.75rem;
		line-height: 1rem;
		font-weight: 600;
		--tw-backdrop-blur: blur(15px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		background: rgba(255, 255, 255, 0.1);
}

	.custom-dropdown-alternative {
		border-color: var(--color-accent-700);
		background-color: var(--color-background-500);
		background-image: unset;
}

	.custom-dropdown-alternative-4 {
		border-width: 0px;
}

	.custom-dropdown-momentum {
		border-width: 0px;
		background-color: var(--color-tertiary-700);
}

	.custom-dropdown-open {
		background: var(--gradient-alternative);
}

	.custom-dropdown-momentum-open {
		border-width: 0px;
		background-color: var(--color-tertiary-700);
		background-image: unset;
}

	.custom-dropdown-open-glass {
		isolation: isolate;
		border-color: double;
		--tw-border-opacity: 1;
		border-color: rgb(169 169 169 / var(--tw-border-opacity, 1));
		--tw-backdrop-blur: blur(15px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		background: rgba(255, 255, 255, 0.1);
}

	.custom-dropdown-open-alternative {
		background-color: var(--color-background-500);
		background-image: unset;
}

	.dropdown-toggle {
		position: relative;
		z-index: 40;
		display: block;
		height: 3.5rem;
		width: 100%;
		cursor: pointer;
		border-radius: 0.5rem;
		padding: 1rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		letter-spacing: 1.5px;
		color: var(--color-text-100);
		font-family: var(--font-main);
		display: flex;
		align-items: center;
		justify-content: space-between;
}

	.dropdown-menu {
		position: relative;
		margin-top: -1.5rem;
		width: 100%;
		overflow: hidden;
		border-radius: 0.5rem;
		background-color: transparent;
		color: var(--color-text-100);
		max-height: 0;
		transition: max-height 500ms ease-in-out;
}

	.dropdown-menu-alternative-4 {
		border-top-left-radius: 0px;
		border-top-right-radius: 0px;
		margin-top: -1.5rem;
		background-color: var(--color-background-500);
}

	.dropdown-menu-alternative-4.expanded-alternative-4 {
		margin-top: 0px;
		background-color: var(--color-tertiary-500);
		padding-top: 0px;
}

	.dropdown-menu-momentum.expanded-momentum {
		margin-top: 0px;
		border-top-left-radius: 0px;
		border-top-right-radius: 0px;
		background-color: var(--color-tertiary-500);
		padding-top: 0px;
}

	.d-flex {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		text-align: start;
}

	.rotated {
		transform: rotate(180deg);
		transition: transform 0.3s ease;
	}

	.text {
		position: relative;
		z-index: 30;
		padding-top: 1rem;
		padding-bottom: 1rem;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		text-align: start;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 400;
		color: var(--color-text-300);
		font-family: var(--font-main);
}

	.additional-content {
		margin-left: auto;
		margin-right: auto;
		width: 100%;
		padding-left: 0.5rem;
		padding-right: 0.5rem;
		padding-top: 1rem;
		padding-bottom: 0.5rem;
}

	.additional-content-alternative-4 {
		padding-top: 0px;
}

	.dropdown-menu-content {
		width: 100%;
		opacity: 0;
		transition: opacity 180ms ease;
}

	.dropdown-menu-content.content-visible {
		opacity: 1;
	}

	.right-title-arrow-container {
		display: flex;
		align-items: center;
		justify-content: flex-end;
		gap: 0.5rem;
}

	@media (prefers-reduced-motion: reduce) {
		.dropdown-menu,
		.dropdown-menu-content,
		.rotated {
			transition: none;
		}
	}

	.link {
		margin-bottom: 0.5rem;
		display: block;
		cursor: pointer;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-500);
		text-decoration-line: none;
}
	.link-button {
		width: 100%;
		-webkit-appearance: none;
		   -moz-appearance: none;
		        appearance: none;
		border-width: 0px;
		background-color: transparent;
		padding: 0px;
		text-align: left;
		font-weight: 400;
		font-family: inherit;
}
	.link:hover {
		color: var(--color-text-100);
}
	.link-alternative-4 {
		color: unset;
		margin-bottom: 0.75rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 400;
	}
</style>
